import { IconList } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import { CategoryFilter } from "@/components/atomic/voc-filter/CategoryFilter";
import { PeriodFilter } from "@/components/atomic/voc-filter/PeriodFilter";
import { ChatAttributeFilter, ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { VocSearchAdvancedFilterTab } from "@/components/ui/VocSearchAdvancedFilterTab";
import { cn } from "@/lib/utils";
import { VocPeriodFilter } from "@/models/vocPeriodFilter";

interface VocSearchFilterProps {
  periodFilter: VocPeriodFilter;
  categories: string[];
  attributeFilters: ChatAttributeFilters;
  tagIds: string[];
  onUpdatePeriodFilter: (updates: Partial<VocPeriodFilter>) => void;
  onUpdateCategories: (categories: string[]) => void;
  onUpdateAttributeFilter: (attributeName: string, filter: ChatAttributeFilter | null) => void;
  onToggleTagId: (tagId: string) => void;
  onClearFilters: () => void;
}

export const VocSearchFilter = (props: Readonly<VocSearchFilterProps>) => {
  const {
    periodFilter,
    categories,
    attributeFilters,
    tagIds,
    onUpdatePeriodFilter,
    onUpdateCategories,
    onUpdateAttributeFilter,
    onToggleTagId,
    onClearFilters,
  } = props;
  const { t } = useTranslation("voiceek_ui");
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"basic" | "advanced">("basic");

  return (
    <div
      ref={filterContainerRef}
      className="bg-white rounded-[10px] shadow-[0px_0px_10px_0px_rgba(41,61,114,0.15)] w-full flex flex-col max-h-[50vh]"
    >
      {/* ヘッダー */}
      <div className="pt-4 px-4 flex justify-end items-center shrink-0">
        <button
          className="text-[14px] font-medium text-[#6888E1] hover:underline hover:cursor-pointer"
          onClick={onClearFilters}
        >
          {t("vocFilter.clear")}
        </button>
      </div>

      <div className="p-4 min-h-0 overflow-hidden flex flex-col">
        {/* タブシステム */}
        <div className="flex gap-[3px] items-center shrink-0">
          <button
            onClick={() => setActiveTab("basic")}
            className={cn(
              "flex items-center px-4 py-2 rounded-tl-[5px] rounded-tr-[5px] transition-colors border-t border-l border-r border-[#6888e1] hover:cursor-pointer",
              activeTab === "basic"
                ? "bg-[#6888e1]"
                : "bg-white",
            )}
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <CalendarIcon
                className={cn(
                  "w-5 h-5",
                  activeTab === "basic" ? "text-white" : "text-[#6888e1]",
                )}
              />
            </span>
            <span
              className={cn(
                "text-[14px] font-semibold ml-1",
                activeTab === "basic" ? "text-white" : "text-[#6888e1]",
              )}
            >
              {t("chat.filter.tabs.periodAndCategory")}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("advanced")}
            className={cn(
              "flex items-center px-4 py-2 rounded-tl-[5px] rounded-tr-[5px] transition-colors border-t border-l border-r border-[#6888e1] hover:cursor-pointer",
              activeTab === "advanced"
                ? "bg-[#6888e1]"
                : "bg-white",
            )}
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <IconList
                className={cn(
                  activeTab === "advanced" ? "stroke-white" : "stroke-[#6888e1]",
                )}
                stroke={2}
              />
            </span>
            <span
              className={cn(
                "text-[14px] font-semibold ml-1",
                activeTab === "advanced" ? "text-white" : "text-[#6888e1]",
              )}
            >
              {t("chat.filter.tabs.detailedFilter")}
            </span>
          </button>
        </div>
        <div className="h-0 w-full border-b border-[#6888e1] shrink-0" />

        {/* コンテンツエリア */}
        <div className="pt-4 flex-1 overflow-auto">
          {activeTab === "basic" && (
            <div className="flex flex-col gap-6">
              <PeriodFilter
                vocPeriodType={periodFilter.type}
                vocPeriodStart={periodFilter.start}
                vocPeriodEnd={periodFilter.end}
                onUpdateState={(updates) =>
                  onUpdatePeriodFilter({
                    type: updates.vocPeriodType,
                    start: updates.vocPeriodStart,
                    end: updates.vocPeriodEnd,
                  })}
                filterContainerRef={filterContainerRef}
              />
              <CategoryFilter
                categories={categories}
                onUpdateCategories={onUpdateCategories}
              />
            </div>
          )}

          {activeTab === "advanced" && (
            <VocSearchAdvancedFilterTab
              attributeFilters={attributeFilters}
              tagIds={tagIds}
              onUpdateAttributeFilter={onUpdateAttributeFilter}
              onToggleTagId={onToggleTagId}
            />
          )}
        </div>
      </div>
    </div>
  );
};
